import {
  Alert,
  Autocomplete,
  Badge,
  Button,
  Group,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { describeRpcError, usePermission } from "@how/app-kit";
import { semanticColor, type SemanticColor } from "@how/design-system";
import {
  ConfirmActionModal,
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
  RecordDrawer,
  useReturnFocus,
  useSearchParamState,
} from "@how/ui";
import {
  devotionalPaths,
  devotionalStatus,
  formatDevotionalDate,
  richTextFromMarkup,
  richTextToMarkup,
  type DevotionalStatus,
} from "@how/website-core";
import {
  devotionalProblems,
  useChurchToday,
  useDeleteDevotional,
  useDevotionals,
  usePublishDevotional,
  useSaveDevotional,
  type DevotionalDraft,
  type DevotionalRow,
} from "./devotional-queries";
import { mediaUrl } from "./media-queries";
import { useAssets, usePublicHost } from "./website-queries";
import { WebsiteTabs } from "./WebsiteTabs";

function notifyError(title: string, error: unknown): void {
  const described = describeRpcError(error);
  notifications.show({
    color: described.kind === "denied" ? "yellow" : "red",
    title,
    message: described.message,
  });
}

/** A devotional's status, as staff see it: a draft, published and waiting for its day, or on the site. */
const STATUS_LABEL: Record<DevotionalStatus, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  published: "Published",
};
// Draft and Published match a page's (page_status); a devotional waiting for its day is informational.
const STATUS_COLOR: Record<DevotionalStatus, SemanticColor> = {
  draft: semanticColor("neutral"),
  scheduled: semanticColor("info"),
  published: semanticColor("success"),
};

const NO_REFLECTION = "Write the reflection before you publish. Save it as a draft until then.";

/**
 * Website → Devotionals (ADR 0077): the church's daily devotionals, as Planning Center Publishing's devotionals and
 * reading plans. Staff with devotionals.manage write one for a day, with its scripture, reflection and prayer, and
 * publish it; one published ahead of its day stays hidden until that day begins in the church's time zone. Staff with
 * devotionals.read alone see the list.
 */
export function DevotionalsPage() {
  const canManage = usePermission("devotionals.manage", { module: "devotionals", write: true });
  const devotionals = useDevotionals();
  const host = usePublicHost();
  const today = useChurchToday();
  const publish = usePublishDevotional();
  const remove = useDeleteDevotional();
  const [search, setSearch] = useSearchParamState("q");
  const [statusFilter, setStatusFilter] = useSearchParamState("status");
  const [seriesFilter, setSeriesFilter] = useSearchParamState("series");
  const [editing, setEditing] = useState<DevotionalRow | "new" | null>(null);
  const [deleting, setDeleting] = useState<DevotionalRow | null>(null);
  const editorFocus = useReturnFocus();
  const editable = canManage.data === true;

  const rows = devotionals.data ?? [];
  const seriesTitles = [
    ...new Set(rows.map((d) => d.seriesTitle).filter((t): t is string => t !== null)),
  ].sort((a, b) => a.localeCompare(b));
  const needle = search.trim().toLowerCase();
  const listed = rows.filter(
    (d) =>
      (statusFilter === "" || devotionalStatus(d, today) === statusFilter) &&
      (seriesFilter === "" || d.seriesTitle === seriesFilter) &&
      (needle === "" ||
        d.title.toLowerCase().includes(needle) ||
        (d.scriptureReference ?? "").toLowerCase().includes(needle) ||
        (d.seriesTitle ?? "").toLowerCase().includes(needle)),
  );
  const siteLink = (path: string) =>
    host.data === null || host.data === undefined ? null : `https://${host.data}${path}`;

  const openEditor = (target: DevotionalRow | "new") => {
    editorFocus.remember();
    setEditing(target);
  };

  const togglePublished = (d: DevotionalRow) => {
    if (!d.isPublished && d.reflection === null) {
      notifications.show({ color: "red", title: "Not published", message: NO_REFLECTION });
      return;
    }
    publish.mutate(
      { devotionalId: d.id, published: !d.isPublished },
      {
        onSuccess: () => {
          notifications.show({
            color: "teal",
            title: d.isPublished
              ? "Taken off the site"
              : d.date > today
                ? `Scheduled for ${formatDevotionalDate(d.date)}`
                : "Published",
            message: d.title,
          });
        },
        onError: (error) => {
          notifyError("Not changed", error);
        },
      },
    );
  };

  return (
    <>
      <PageHeader
        title="Website"
        description="Daily devotionals on your website and in the member portal."
        actions={
          editable ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                openEditor("new");
              }}
            >
              Add devotional
            </Button>
          ) : null
        }
      />
      <WebsiteTabs active="devotionals" />
      <FilterBar
        search={search}
        onSearchChange={(value) => {
          setSearch(value === "" ? undefined : value);
        }}
        searchPlaceholder="Search by title, scripture or series"
        filters={
          <>
            <Select
              aria-label="Status"
              placeholder="Every status"
              clearable
              data={[
                { value: "draft", label: "Draft" },
                { value: "scheduled", label: "Scheduled" },
                { value: "published", label: "Published" },
              ]}
              value={statusFilter === "" ? null : statusFilter}
              onChange={(value) => {
                setStatusFilter(value ?? undefined);
              }}
            />
            <Select
              aria-label="Series"
              placeholder="Every series"
              clearable
              data={seriesTitles}
              value={seriesFilter === "" ? null : seriesFilter}
              onChange={(value) => {
                setSeriesFilter(value ?? undefined);
              }}
            />
          </>
        }
      />
      {devotionals.isPending ? (
        <LoadingState rows={5} />
      ) : devotionals.isError ? (
        <ErrorState
          message={devotionals.error.message}
          onRetry={() => void devotionals.refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No devotionals yet"
          description="Write your first devotional. Publish it and it appears on your website at /devotional and in the member portal on its day."
        />
      ) : listed.length === 0 ? (
        <EmptyState title="No devotionals match" description="Try another status or series." />
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Devotional</Table.Th>
                <Table.Th>Date</Table.Th>
                <Table.Th>Series</Table.Th>
                <Table.Th>Author</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th aria-label="Actions" />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {listed.map((d) => {
                const status = devotionalStatus(d, today);
                const link = siteLink(devotionalPaths.devotional(d.slug));
                const publishLabel = d.isPublished
                  ? "Unpublish"
                  : d.date > today
                    ? "Schedule"
                    : "Publish";
                return (
                  <Table.Tr key={d.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {d.title}
                      </Text>
                      {d.scriptureReference === null ? null : (
                        <Text size="xs" c="dimmed">
                          {d.scriptureReference}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td className="how-numeric" style={{ whiteSpace: "nowrap" }}>
                      {formatDevotionalDate(d.date, "en-US", "short")}
                    </Table.Td>
                    <Table.Td>{d.seriesTitle ?? ""}</Table.Td>
                    <Table.Td>{d.authorName ?? ""}</Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={STATUS_COLOR[status]}>
                        {STATUS_LABEL[status]}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        {status === "published" && link !== null ? (
                          <Button
                            component="a"
                            href={link}
                            target="_blank"
                            rel="noreferrer"
                            size="compact-xs"
                            variant="subtle"
                            aria-label={`View ${d.title} on the website (opens in a new tab)`}
                          >
                            View
                          </Button>
                        ) : null}
                        {editable ? (
                          <>
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              aria-label={`Edit ${d.title}`}
                              onClick={() => {
                                openEditor(d);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              aria-label={`${publishLabel} ${d.title}`}
                              onClick={() => {
                                togglePublished(d);
                              }}
                            >
                              {publishLabel}
                            </Button>
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              color="red"
                              aria-label={`Delete ${d.title}`}
                              onClick={() => {
                                setDeleting(d);
                              }}
                            >
                              Delete
                            </Button>
                          </>
                        ) : null}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {editing === null ? null : (
        <DevotionalDrawer
          devotional={editing === "new" ? null : editing}
          seriesTitles={seriesTitles}
          today={today}
          onClose={() => {
            setEditing(null);
            editorFocus.restore();
          }}
        />
      )}
      <ConfirmActionModal
        opened={deleting !== null}
        onClose={() => {
          setDeleting(null);
        }}
        title="Delete this devotional"
        message={`Delete ${deleting?.title ?? ""}? It leaves the website and the member portal at once. This cannot be undone.`}
        confirmLabel="Delete devotional"
        danger
        loading={remove.isPending}
        onConfirm={async () => {
          if (deleting === null) return;
          try {
            await remove.mutateAsync(deleting.id);
            notifications.show({
              color: "teal",
              title: "Devotional deleted",
              message: deleting.title,
            });
          } catch (error) {
            notifyError("Not deleted", error);
          }
          setDeleting(null);
        }}
      />
    </>
  );
}

// --------------------------------------------------------------------------------------------------- the devotional

function draftOf(devotional: DevotionalRow | null, today: string): DevotionalDraft {
  return {
    title: devotional?.title ?? "",
    date: devotional?.date ?? today,
    seriesTitle: devotional?.seriesTitle ?? "",
    scriptureReference: devotional?.scriptureReference ?? "",
    scriptureText: devotional?.scriptureText ?? "",
    scriptureVersion: devotional?.scriptureVersion ?? "",
    reflection: devotional?.reflection ?? null,
    prayer: devotional?.prayer ?? "",
    imagePath: devotional?.imagePath ?? null,
    imageAlt: devotional?.imageAlt ?? null,
    authorName: devotional?.authorName ?? "",
    authorPersonId: devotional?.authorPersonId ?? null,
  };
}

function DevotionalDrawer({
  devotional,
  seriesTitles,
  today,
  onClose,
}: {
  readonly devotional: DevotionalRow | null;
  readonly seriesTitles: readonly string[];
  readonly today: string;
  readonly onClose: () => void;
}) {
  const save = useSaveDevotional();
  const publish = usePublishDevotional();
  const assets = useAssets();
  const [draft, setDraft] = useState<DevotionalDraft>(() => draftOf(devotional, today));
  const [reflectionText, setReflectionText] = useState(() =>
    devotional?.reflection === null || devotional?.reflection === undefined
      ? ""
      : richTextToMarkup(devotional.reflection),
  );
  const [touched, setTouched] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problems = devotionalProblems(draft);
  const shown = (key: string) => (touched ? problems[key] : undefined);
  const set = (patch: Partial<DevotionalDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  };
  const alreadyPublished = devotional?.isPublished === true;
  const ahead = draft.date > today;
  const busy = save.isPending || publish.isPending;
  const when = /^\d{4}-\d{2}-\d{2}$/.test(draft.date) ? formatDevotionalDate(draft.date) : "";

  const submit = async (andPublish: boolean) => {
    setTouched(true);
    setPublishing(andPublish);
    setError(null);
    if (Object.keys(problems).length > 0) return;
    if (andPublish && draft.reflection === null) return;
    try {
      const id = await save.mutateAsync({ devotionalId: devotional?.id ?? null, draft });
      if (andPublish) {
        try {
          await publish.mutateAsync({ devotionalId: id, published: true });
        } catch (e) {
          notifyError("Saved, but not published", e);
          onClose();
          return;
        }
      }
      const live = andPublish || alreadyPublished;
      notifications.show({
        color: "teal",
        title: !live
          ? devotional === null
            ? "Devotional added"
            : "Devotional saved"
          : ahead
            ? `Scheduled for ${when}`
            : andPublish
              ? "Published"
              : "Devotional saved",
        message: live
          ? draft.title.trim()
          : `${draft.title.trim()} is a draft. Publish it from the list when it is ready.`,
      });
      onClose();
    } catch (e) {
      setError(describeRpcError(e).message);
    }
  };

  const reflectionProblem =
    touched && publishing && draft.reflection === null ? NO_REFLECTION : undefined;

  return (
    <RecordDrawer
      opened
      onClose={onClose}
      title={devotional === null ? "Add devotional" : "Edit devotional"}
      closeButtonProps={{ "aria-label": "Close" }}
    >
      <Stack gap="md">
        <TextInput
          label="Title"
          required
          maxLength={160}
          value={draft.title}
          error={shown("title")}
          onChange={(event) => {
            set({ title: event.currentTarget.value });
          }}
        />
        <Group grow align="flex-start">
          <TextInput
            type="date"
            label="Date"
            required
            description="The day it is for. Published ahead of its day, it stays hidden until that day begins in your church's time zone."
            value={draft.date}
            error={shown("date")}
            onChange={(event) => {
              set({ date: event.currentTarget.value });
            }}
          />
          <Autocomplete
            label="Series or plan"
            description="Choose one you have used, or type a new name."
            maxLength={120}
            data={[...seriesTitles]}
            value={draft.seriesTitle}
            error={shown("seriesTitle")}
            onChange={(value) => {
              set({ seriesTitle: value });
            }}
          />
        </Group>
        <Title order={2} size="h5">
          Scripture
        </Title>
        <Group grow align="flex-start">
          <TextInput
            label="Scripture reference"
            placeholder="John 10:10"
            maxLength={120}
            value={draft.scriptureReference}
            error={shown("scriptureReference")}
            onChange={(event) => {
              set({ scriptureReference: event.currentTarget.value });
            }}
          />
          <TextInput
            label="Translation"
            description="Such as WEB or KJV. Quote only a translation you may publish."
            maxLength={40}
            value={draft.scriptureVersion}
            error={shown("scriptureVersion")}
            onChange={(event) => {
              set({ scriptureVersion: event.currentTarget.value });
            }}
          />
        </Group>
        <Textarea
          label="Passage"
          description="The verses, as you want them printed."
          autosize
          minRows={3}
          maxLength={6000}
          value={draft.scriptureText}
          error={shown("scriptureText")}
          onChange={(event) => {
            set({ scriptureText: event.currentTarget.value });
          }}
        />
        <Title order={2} size="h5">
          Reflection and prayer
        </Title>
        <Textarea
          label="Reflection"
          description="What people read. Blank line between paragraphs. ### heading (such as Reflect before a question), - list item, 1. numbered item, > quote, **bold**, *italic*, [link text](https://…)."
          autosize
          minRows={6}
          value={reflectionText}
          error={reflectionProblem}
          onChange={(event) => {
            const text = event.currentTarget.value;
            setReflectionText(text);
            const nodes = richTextFromMarkup(text);
            set({ reflection: nodes.length === 0 ? null : nodes });
          }}
        />
        <Textarea
          label="Prayer"
          autosize
          minRows={2}
          maxLength={4000}
          value={draft.prayer}
          error={shown("prayer")}
          onChange={(event) => {
            set({ prayer: event.currentTarget.value });
          }}
        />
        <Title order={2} size="h5">
          Image and author
        </Title>
        <Select
          label="Image"
          description="From your images, with its description for people who cannot see it."
          placeholder="No image"
          clearable
          disabled={assets.isError}
          data={(assets.data ?? []).map((a) => ({ value: a.storage_path, label: a.alt_text }))}
          value={draft.imagePath}
          onChange={(value) => {
            const asset = (assets.data ?? []).find((a) => a.storage_path === value);
            set({ imagePath: value, imageAlt: asset?.alt_text ?? null });
          }}
        />
        {draft.imagePath === null ? null : (
          <img
            src={mediaUrl(draft.imagePath)}
            alt=""
            style={{ maxHeight: 120, borderRadius: 8, alignSelf: "flex-start" }}
          />
        )}
        <TextInput
          label="Author"
          description="The name shown on the devotional."
          maxLength={120}
          value={draft.authorName}
          error={shown("authorName")}
          onChange={(event) => {
            set({ authorName: event.currentTarget.value });
          }}
        />
        {error === null ? null : (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          {alreadyPublished ? (
            <Button loading={busy} onClick={() => void submit(false)}>
              Save devotional
            </Button>
          ) : (
            <>
              <Button
                variant="default"
                loading={busy && !publishing}
                disabled={busy && publishing}
                onClick={() => void submit(false)}
              >
                Save draft
              </Button>
              <Button
                loading={busy && publishing}
                disabled={busy && !publishing}
                onClick={() => void submit(true)}
              >
                {ahead ? "Save and schedule" : "Save and publish"}
              </Button>
            </>
          )}
        </Group>
      </Stack>
    </RecordDrawer>
  );
}
